// Browser-side Linear service
// Uses proxiedFetch for CORS-safe API calls from the browser
// Linear uses a GraphQL API at https://api.linear.app/graphql

import { proxiedFetch } from "./cors-proxy";
import type {
  LinearIntegrationSettings,
  LinearIssue,
  LinearTeam,
  LinearActivityEvent,
} from "./types";

interface LinearConfig {
  apiKey?: string;
  /** OAuth2 Bearer access token — takes precedence over apiKey when provided */
  accessToken?: string;
}

export class BrowserLinearService {
  private authHeader: string;

  constructor(config: LinearConfig) {
    // OAuth2 Bearer token takes precedence over PAT
    if (config.accessToken?.trim()) {
      this.authHeader = `Bearer ${config.accessToken.trim()}`;
    } else {
      this.authHeader = (config.apiKey ?? '').trim();
    }
  }

  private async graphql<T>(query: string, variables?: Record<string, unknown>): Promise<T> {
    const res = await proxiedFetch("https://api.linear.app/graphql", {
      method: "POST",
      headers: {
        Authorization: this.authHeader,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ query, variables }),
    });

    if (!res.ok) {
      throw new Error(`Linear API error ${res.status}: ${res.statusText}`);
    }

    const json = await res.json();
    if (json.errors?.length) {
      throw new Error(`Linear GraphQL error: ${json.errors[0].message}`);
    }

    return json.data as T;
  }

  async getViewer(): Promise<{ id: string; name: string; email: string; avatarUrl?: string }> {
    const data = await this.graphql<{
      viewer: { id: string; name: string; email: string; avatarUrl?: string };
    }>(`query { viewer { id name email avatarUrl } }`);
    return data.viewer;
  }

  async getTeams(): Promise<LinearTeam[]> {
    const data = await this.graphql<{
      teams: { nodes: LinearTeam[] };
    }>(`query {
      teams {
        nodes {
          id
          name
          key
          description
          icon
        }
      }
    }`);
    return data.teams.nodes;
  }

  async getTeamIssues(
    teamId: string,
    opts: { limit?: number; includeCompleted?: boolean } = {}
  ): Promise<LinearIssue[]> {
    const limit = opts.limit || 50;
    const filter = opts.includeCompleted
      ? `{ team: { id: { eq: "${teamId}" } } }`
      : `{ team: { id: { eq: "${teamId}" } }, completedAt: { null: true } }`;

    const data = await this.graphql<{
      issues: { nodes: RawLinearIssue[] };
    }>(`query {
      issues(
        first: ${limit}
        filter: ${filter}
        orderBy: updatedAt
      ) {
        nodes {
          id
          identifier
          title
          description
          priority
          priorityLabel
          createdAt
          updatedAt
          completedAt
          state { id name color type }
          assignee { id name displayName email avatarUrl }
          creator { id name displayName }
          labels { nodes { id name color } }
          cycle { id name number }
          project { id name icon }
          team { id name key }
        }
      }
    }`);

    return data.issues.nodes.map(mapIssue);
  }

  async getRecentActivity(
    teamId?: string,
    lookbackDays: number = 7
  ): Promise<LinearActivityEvent[]> {
    const since = new Date(Date.now() - lookbackDays * 86400000).toISOString();
    const teamFilter = teamId
      ? `filter: { team: { id: { eq: "${teamId}" } }, updatedAt: { gte: "${since}" } }`
      : `filter: { updatedAt: { gte: "${since}" } }`;

    const data = await this.graphql<{
      issues: { nodes: RawLinearIssue[] };
    }>(`query {
      issues(
        first: 100
        ${teamFilter}
        orderBy: updatedAt
      ) {
        nodes {
          id
          identifier
          title
          updatedAt
          state { id name color type }
          assignee { id name displayName avatarUrl }
          team { id name key }
          history(first: 20) {
            nodes {
              id
              createdAt
              fromState { name }
              toState { name }
              fromAssignee { name }
              toAssignee { name }
              actor { id name avatarUrl }
            }
          }
        }
      }
    }`);

    const events: LinearActivityEvent[] = [];

    for (const issue of data.issues.nodes) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const history = (issue as any).history?.nodes || [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      for (const entry of history) {
        const eventDate = new Date(entry.createdAt);
        if (eventDate < new Date(since)) continue;

        let type = "field_change";
        if (entry.fromState && entry.toState) type = "status_change";
        else if (entry.fromAssignee || entry.toAssignee) type = "assignment";

        events.push({
          id: entry.id,
          timestamp: entry.createdAt,
          type,
          issueIdentifier: issue.identifier,
          issueSummary: issue.title,
          author: {
            displayName: entry.actor?.name || "Unknown",
            avatarUrl: entry.actor?.avatarUrl,
          },
          fromState: entry.fromState?.name,
          toState: entry.toState?.name,
          fromAssignee: entry.fromAssignee?.name,
          toAssignee: entry.toAssignee?.name,
        });
      }
    }

    events.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    return events;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async getIssueDetail(issueIdentifier: string): Promise<any> {
    const data = await this.graphql<{ issue: any }>(`query($id: String!) {
      issue(id: $id) {
        id
        identifier
        title
        description
        url
        priority
        priorityLabel
        estimate
        createdAt
        updatedAt
        completedAt
        canceledAt
        startedAt
        dueDate
        snoozedUntilAt
        state { id name color type }
        assignee { id name displayName email avatarUrl }
        creator { id name displayName avatarUrl }
        team { id name key }
        project { id name icon color state }
        cycle { id name number startsAt endsAt }
        parent { id identifier title state { name color } assignee { name } }
        labels { nodes { id name color } }
        children { nodes { id identifier title state { name color } assignee { name displayName } priority priorityLabel } }
        relations { nodes { id type relatedIssue { id identifier title state { name color } assignee { name } } } }
        comments(first: 50) { nodes { id body createdAt updatedAt user { id name displayName avatarUrl } } }
        attachments { nodes { id title subtitle url metadata createdAt creator { name } } }
        history(first: 30) {
          nodes {
            id
            createdAt
            fromState { name color }
            toState { name color }
            fromAssignee { name }
            toAssignee { name }
            actor { id name avatarUrl }
            fromPriority
            toPriority
            fromEstimate
            toEstimate
          }
        }
      }
    }`, { id: issueIdentifier });

    return data.issue;
  }
}

// ==================== Helpers ====================

interface RawLinearIssue {
  id: string;
  identifier: string;
  title: string;
  description?: string;
  priority: number;
  priorityLabel: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  state: { id: string; name: string; color: string; type: string };
  assignee?: { id: string; name: string; displayName: string; email?: string; avatarUrl?: string };
  creator?: { id: string; name: string; displayName: string };
  labels: { nodes: { id: string; name: string; color: string }[] };
  cycle?: { id: string; name: string; number: number };
  project?: { id: string; name: string; icon?: string };
  team: { id: string; name: string; key: string };
}

function mapIssue(raw: RawLinearIssue): LinearIssue {
  return {
    id: raw.id,
    identifier: raw.identifier,
    title: raw.title,
    description: raw.description,
    state: raw.state,
    assignee: raw.assignee,
    creator: raw.creator,
    priority: raw.priority,
    priorityLabel: raw.priorityLabel,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
    completedAt: raw.completedAt,
    labels: raw.labels?.nodes || [],
    cycle: raw.cycle,
    project: raw.project,
    team: raw.team,
  };
}

// ==================== Validation ====================

export async function validateLinearCredentials(
  settings: Pick<LinearIntegrationSettings, "apiKey"> & { accessToken?: string }
): Promise<{ valid: boolean; error?: string; user?: { id: string; name: string; email: string; avatarUrl?: string } }> {
  try {
    const svc = new BrowserLinearService({
      apiKey: settings.apiKey,
      accessToken: settings.accessToken,
    });
    const viewer = await svc.getViewer();
    return { valid: true, user: viewer };
  } catch (err: unknown) {
    const e = err as Error;
    return { valid: false, error: e.message || "Linear validation failed" };
  }
}
